/**
 * StorageService - Platform-agnostic storage abstraction
 * 
 * For web: uses localStorage
 * For RN migration: replace implementation with AsyncStorage
 * 
 * All storage keys should be prefixed with 'cashee_' to avoid conflicts
 */

export interface IStorageService {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  getAllKeys(): Promise<string[]>;
}

class LocalStorageService implements IStorageService {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('StorageService.getItem error:', error);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('StorageService.setItem error:', error);
      throw error;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('StorageService.removeItem error:', error);
      throw error;
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('StorageService.getAllKeys error:', error);
      return [];
    }
  }
}

// Singleton instance
export const storageService: IStorageService = new LocalStorageService();

// Helper methods for common patterns
export const storageHelpers = {
  async getJSON<T>(key: string, defaultValue: T): Promise<T> {
    const value = await storageService.getItem(key);
    if (!value) return defaultValue;
    try {
      return JSON.parse(value) as T;
    } catch {
      return defaultValue;
    }
  },

  async setJSON<T>(key: string, value: T): Promise<void> {
    await storageService.setItem(key, JSON.stringify(value));
  },

  async getBoolean(key: string, defaultValue: boolean): Promise<boolean> {
    const value = await storageService.getItem(key);
    return value === 'true' ? true : value === 'false' ? false : defaultValue;
  },

  async setBoolean(key: string, value: boolean): Promise<void> {
    await storageService.setItem(key, value.toString());
  },
};

// Storage keys constants
export const STORAGE_KEYS = {
  EXPENSE_QUEUE: 'cashee_expense_queue',
  ONBOARDING_COMPLETED: 'cashee_onboarding_completed',
  CONSENT_GIVEN: 'cashee_consent_given',
  LAST_SYNC: 'cashee_last_sync',
  OFFLINE_MODE: 'cashee_offline_mode',
  LAST_SEEN_VERSION: 'cashee_last_seen_version',
} as const;
